import React from 'react';
import PropTypes from 'prop-types';
import {useSelector} from 'react-redux';
import {merge} from 'lodash-es';

import {Box, Grid, GridItem, MenuList} from '@spothero/ui';

import {useAnimationContext} from 'common/animation/AnimationContext';
import {selectSelectedFacility} from 'pages/facilities/store/facilities-selectors';

import FacilityInformationDisplay from './FacilityInformationDisplay';
import NavigationMenuGroup from './NavigationMenuGroup';

const getMenuListStyleProps = ({collapsed, isMultiSelect}) => ({
    display: 'flex',
    justifyContent: 'flex-start',
    flexDirection: 'column',
    gap: 4,
    height: '100%',
    maxHeight: {base: 'calc(100vh - 3.5rem)', desktop: 'auto'},
    minHeight: isMultiSelect ? '27rem' : '30rem',
    paddingLeft: collapsed ? 'calc(4.375rem + 2rem)' : 'calc(21.875rem + 2rem)',
    overflowY: 'auto',
});

const getGridItemStyleProps = ({collapsed, numberOfItems}) => ({
    colSpan: {base: collapsed ? 4 : 6, desktop: 4},
    rowSpan: numberOfItems > 2 ? 2 : 1,
    position: 'relative',
    paddingRight: 4,
    // The following styles add a right border to all but the last two items
    _after: {
        content: {base: collapsed ? '""' : 'none', desktop: '""'},
        position: 'absolute',
        right: 0,
        top: 0,
        width: '1px',
        maxHeight: '26rem',
        height: '100%',
        backgroundColor: 'gray.medium',
    },
    _last: {
        _after: {
            display: 'none',
        },
    },
    sx: {
        '&:nth-of-type(n + 3)': {
            _after: {
                display: 'none',
            },
        },
    },
});

const NavigationDropDownMenu = ({isMultiSelect, menuContentArray}) => {
    const {title, address, physicalAddress} = useSelector(
        selectSelectedFacility
    );

    // TODO: We need to normalize the address/physicalAdress object to have the same keys
    const {streetAddress, city, state, zipcode} = merge(
        {},
        {
            city: physicalAddress?.city,
            state: physicalAddress?.state,
            streetAddress:
                physicalAddress?.streetAddress || physicalAddress?.street,
            zipcode: physicalAddress?.zipcode || physicalAddress?.zip,
        },
        {
            city: address?.city,
            state: address?.state,
            streetAddress: address?.street || address?.streetAddress,
            zipcode: address?.zip || address?.zipcode,
        }
    );
    const addressString = `${streetAddress && streetAddress} ${
        city && city
    }, ${state && state} ${zipcode && zipcode}`;
    const {
        collapsed: [collapsed],
    } = useAnimationContext();

    return (
        <MenuList {...getMenuListStyleProps({collapsed, isMultiSelect})}>
            <Box margin="0 auto" width="full" maxWidth="90rem" height="100%">
                {!isMultiSelect && (
                    <FacilityInformationDisplay
                        title={title}
                        address={addressString}
                    />
                )}
                <Grid isCompact width="100%" autoFlow="row dense">
                    {menuContentArray.map((menuContentArrayItem, index) => {
                        const {heading, submenu} = menuContentArrayItem;

                        return (
                            <GridItem
                                key={index}
                                {...getGridItemStyleProps({
                                    collapsed,
                                    numberOfItems: submenu.length,
                                })}
                            >
                                <NavigationMenuGroup
                                    heading={heading}
                                    submenu={submenu}
                                />
                            </GridItem>
                        );
                    })}
                </Grid>
            </Box>
        </MenuList>
    );
};

NavigationDropDownMenu.propTypes = {
    isMultiSelect: PropTypes.bool.isRequired,
    menuContentArray: PropTypes.arrayOf(
        PropTypes.shape({
            heading: PropTypes.string.isRequired,
            submenu: PropTypes.arrayOf(
                PropTypes.shape({
                    label: PropTypes.string.isRequired,
                    subtitle: PropTypes.string,
                    route: PropTypes.string.isRequired,
                    isDisabled: PropTypes.bool,
                    isNew: PropTypes.bool,
                })
            ),
        })
    ).isRequired,
};

export default NavigationDropDownMenu;
